import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { MapPin } from "lucide-react";

interface CityOption {
  name: string;
  slug: string;
}

interface CityAutocompleteProps {
  onSelect: (city: string, citySlug: string | null) => void;
  value: string;
  required?: boolean;
}

export function CityAutocomplete({ onSelect, value, required }: CityAutocompleteProps) {
  const [query, setQuery] = useState(value);
  const [cities, setCities] = useState<CityOption[]>([]);
  const [open, setOpen] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualCity, setManualCity] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (query.length < 1) {
      setCities([]);
      return;
    }
    const timeout = setTimeout(async () => {
      const { data } = await supabase
        .from("cities")
        .select("name, slug")
        .eq("is_active", true)
        .ilike("name", `%${query}%`)
        .order("name")
        .limit(8);
      setCities(data ?? []);
    }, 200);
    return () => clearTimeout(timeout);
  }, [query]);

  if (showManual) {
    return (
      <div className="space-y-2">
        <Input
          value={manualCity}
          onChange={(e) => {
            setManualCity(e.target.value);
            onSelect(e.target.value, null);
          }}
          placeholder="Escribe tu ciudad"
          required={required}
        />
        <button
          type="button"
          className="text-xs text-primary hover:underline"
          onClick={() => {
            setShowManual(false);
            setManualCity("");
            setQuery("");
            onSelect("", null);
          }}
        >
          ← Volver a buscar
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          // Clear selection when typing
          if (value && e.target.value !== value) {
            onSelect("", null);
          }
        }}
        onFocus={() => query.length >= 1 && setOpen(true)}
        placeholder="Busca tu ciudad…"
        required={required && !value}
        autoComplete="off"
      />
      {/* Hidden input for native form validation */}
      <input type="hidden" value={value} required={required} />

      {open && (cities.length > 0 || query.length >= 1) && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md max-h-56 overflow-y-auto">
          {cities.map((c) => (
            <button
              key={c.slug}
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent text-left"
              onClick={() => {
                setQuery(c.name);
                onSelect(c.name, c.slug);
                setOpen(false);
              }}
            >
              <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              {c.name}
            </button>
          ))}
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-accent border-t text-left"
            onClick={() => {
              setShowManual(true);
              setOpen(false);
              onSelect("", null);
            }}
          >
            Mi ciudad no aparece
          </button>
        </div>
      )}
    </div>
  );
}
